import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subscription, Subject } from 'rxjs';

type Message =
  | { type: 'ready' }
  | { type: 'imageSelected'; payload: number }
  | { type: 'pictures'; payload: any[] }
  | { type: 'moveFlag'; payload: { flag: any; x: number; y: number } }
  | { type: 'editFlag'; payload: { flag: any; clientX: number; clientY: number } }
  | { type: 'updateAnswer'; payload: any }
  | { type: 'pinFlag'; payload: any }
  | { type: 'navigatePicture'; payload: any };

@Injectable({ providedIn: 'root' })
export class ViewerWindowService implements OnDestroy {
  private popupWindow: Window | null = null;
  private pollSub: Subscription | null = null;
  private isOpen$ = new BehaviorSubject<boolean>(false);
  private readonly pollInterval = 1000;

  private channel = new BroadcastChannel('viewer_sync');

  private image$ = new BehaviorSubject<number | null>(null);
  private pictures$ = new BehaviorSubject<any[] | null>(null);
  private movedFlag$ = new Subject<any | null>();
  private updatedAnswer$ = new Subject<any | null>();
  private navigatedPicture$ = new Subject<'prev' | 'next' | null>();
  private pinnedFlag$ = new Subject<any | null>();
  private ready$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.channel.onmessage = (event: MessageEvent<Message>) => this.handleMessage(event.data);
  }

  private addWindowListeners(): void {
    window.addEventListener('beforeunload', this.close.bind(this));
    window.addEventListener('unload', this.close.bind(this));
  }

  private removeWindowListeners(): void {
    window.removeEventListener('beforeunload', this.close.bind(this));
    window.removeEventListener('unload', this.close.bind(this));
  }

  open(url = '/inspection/grid-popup', name = 'lcp_grid_popup', features = 'width=800,height=600'): Promise<Window> {
    this.addWindowListeners();
    return new Promise((resolve, reject) => {
      if (!this.popupWindow || this.popupWindow.closed) {
        this.popupWindow = window.open(url, name, features);
        this.ready$.next(false);

        if (!this.popupWindow) {
          reject(new Error('Failed to open popup window'));
          return;
        }

        const timeout = setTimeout(() => {
          reject(new Error('Popup window load timeout'));
        }, 10000); // 10 second timeout

        const originalHandler: any = this.channel.onmessage;
        this.channel.onmessage = (event: MessageEvent<Message>) => {
          clearTimeout(timeout);
          this.ready$.next(true);
          this.startPolling();
          resolve(this.popupWindow);
          // Restore original handler after ready
          this.channel.onmessage = originalHandler;
          this.channel.onmessage(event);
        };

        const checkWindow = () => {
          if (this.popupWindow?.closed) {
            clearTimeout(timeout);
            reject(new Error('Window was closed'));
            return;
          }

          try {
            // Check if window is loaded and ready
            if (this.popupWindow?.document?.readyState === 'complete') {
              // Window loaded, waiting for ready signal
              setTimeout(checkWindow, 100);
            } else {
              setTimeout(checkWindow, 100);
            }
          } catch (e) {
            // Cross-origin errors will throw here until window is loaded
            setTimeout(checkWindow, 100);
          }
        };

        checkWindow();
      } else {
        this.popupWindow.focus();
        resolve(this.popupWindow);
      }
    });
  }

  isOpen(): boolean {
    return this.isOpen$.value;
  }

  close() {
    this.popupWindow?.close();
    this.popupWindow = null;
    this.pollSub?.unsubscribe();
    this.isOpen$.next(false);
    this.removeWindowListeners();
  }

  getWindowStatus(): Observable<boolean> {
    return this.isOpen$.asObservable();
  }

  setReady() {
    this.channel.postMessage({ type: 'ready' });
  }

  // --- Data Sync Methods ---
  private handleMessage(message: Message) {
    console.log('Received:', message);
    switch (message.type) {
      case 'imageSelected':
        this.image$.next(message.payload);
        break;
      case 'pictures':
        this.pictures$.next(message.payload);
        break;
      case 'moveFlag':
        this.movedFlag$.next(message.payload);
        break;
      case 'updateAnswer':
        this.updatedAnswer$.next(message.payload);
        break;
      case 'navigatePicture':
        this.navigatedPicture$.next(message.payload);
        break;
      case 'pinFlag':
        this.pinnedFlag$.next(message.payload);
        break;
    }
  }

  selectImage(id: number) {
    this.channel.postMessage({ type: 'imageSelected', payload: id });
    this.image$.next(id);
  }

  setPictures(pictures: any[]) {
    this.channel.postMessage({ type: 'pictures', payload: pictures });
    this.pictures$.next(pictures);
  }

  getSelectedImage(): Observable<number | null> {
    return this.image$.asObservable();
  }

  getPictures(): Observable<any[] | null> {
    return this.pictures$.asObservable();
  }

  moveFlag(event: { flag: any; x: number; y: number }) {
    this.channel.postMessage({ type: 'moveFlag', payload: event });
    this.movedFlag$.next(event);
  }

  getMovedFlag(): Observable<{ flag: any; x: number; y: number }> {
    return this.movedFlag$.asObservable();
  }

  updateAnswer(event: any) {
    this.channel.postMessage({ type: 'updateAnswer', payload: event });
    this.updatedAnswer$.next(event);
  }

  getUpdatedAnswer(): Observable<{ answer: any }> {
    return this.updatedAnswer$.asObservable();
  }

  navigatePicture(event: 'prev' | 'next') {
    this.channel.postMessage({ type: 'navigatePicture', payload: event });
    this.navigatedPicture$.next(event);
  }

  getNavigatedPicture(): Observable<'prev' | 'next'> {
    return this.navigatedPicture$.asObservable();
  }

  pinFlag(event: any) {
    this.channel.postMessage({ type: 'pinFlag', payload: event });
    this.pinnedFlag$.next(event);
  }

  getPinnedFlag(): Observable<any> {
    return this.pinnedFlag$.asObservable();
  }

  private startPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(this.pollInterval).subscribe(() => {
      const isAlive = this.popupWindow && !this.popupWindow.closed;
      this.isOpen$.next(isAlive);
      if (!isAlive) {
        this.pollSub?.unsubscribe();
        this.popupWindow = null;
      }
    });
    this.isOpen$.next(true);
  }

  ngOnDestroy(): void {
    this.removeWindowListeners();
    this.close();
    this.channel.close();
    this.pollSub?.unsubscribe();
  }
}
